import axios from 'axios';
import { isRouteErrorResponse } from 'react-router-dom';
import type { AppErrorDetails, AppErrorKind } from './appErrorTypes';
import { DEFAULT_APP_ERROR, NOT_FOUND_ERROR } from './appErrorTypes';
import { isAuthErrorStatus } from '../utils/sessionExpiry';

const KIND_TITLES: Record<AppErrorKind, string> = {
  config: 'Configuration required',
  auth: 'Authentication required',
  session: 'Session expired',
  network: 'Request failed',
  not_found: 'Page not found',
  runtime: 'Application error',
  unknown: 'Something went wrong',
};

function readAxiosMessage(error: axios.AxiosError): string | undefined {
  const data = error.response?.data;
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const msg =
      (typeof record.message === 'string' && record.message) ||
      (typeof record.error === 'string' && record.error) ||
      (typeof record.detail === 'string' && record.detail);
    if (msg?.trim()) {
      return msg.trim();
    }
  }
  return error.message?.trim() || undefined;
}

export function createAppError(
  kind: AppErrorKind,
  message: string,
  overrides: Partial<Omit<AppErrorDetails, 'kind' | 'message'>> = {},
): AppErrorDetails {
  return {
    kind,
    title: overrides.title ?? KIND_TITLES[kind],
    message,
    ...overrides,
  };
}

export function createConfigError(message: string, detail?: string): AppErrorDetails {
  return createAppError('config', message, {
    detail,
    actions: [{ id: 'settings', label: 'Configure settings', primary: true }, { id: 'retry', label: 'Retry' }],
  });
}

export function normalizeAppError(
  error: unknown,
  overrides?: Partial<AppErrorDetails>,
): AppErrorDetails {
  let base: AppErrorDetails = DEFAULT_APP_ERROR;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      base = { ...NOT_FOUND_ERROR };
    } else {
      base = createAppError('unknown', error.statusText || `Error ${error.status}`, {
        statusCode: error.status,
        detail: typeof error.data === 'string' ? error.data : undefined,
      });
    }
  } else if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message = readAxiosMessage(error) || 'A network request failed. Check your connection and try again.';
    if (isAuthErrorStatus(status)) {
      base = createAppError('session', message, { statusCode: status });
    } else if (status === 404) {
      base = { ...NOT_FOUND_ERROR, message };
    } else {
      base = createAppError('network', message, { statusCode: status });
    }
  } else if (error instanceof Error) {
    base = createAppError('runtime', error.message || 'An unexpected application error occurred.', {
      detail: import.meta.env.DEV ? error.stack : undefined,
    });
  } else if (typeof error === 'string' && error.trim()) {
    base = createAppError('unknown', error.trim());
  } else if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message =
      (typeof record.message === 'string' && record.message) ||
      (typeof record.error === 'string' && record.error);
    if (message?.trim()) {
      const kind =
        (typeof record.kind === 'string' ? record.kind : undefined) as AppErrorKind | undefined;
      base = createAppError(kind && kind in KIND_TITLES ? kind : 'unknown', message.trim(), {
        title: typeof record.title === 'string' ? record.title : undefined,
        detail: typeof record.detail === 'string' ? record.detail : undefined,
        statusCode: typeof record.statusCode === 'number' ? record.statusCode : undefined,
      });
    }
  }

  if (!overrides) {
    return base;
  }

  return {
    ...base,
    ...overrides,
    title: overrides.title ?? base.title,
    message: overrides.message ?? base.message,
  };
}
